'use client'

import { Body, Button, Caption, H3 } from '@material-bank/mb3-components'
import {
  Form,
  Input,
  z,
  createFormSchema,
  SubmitHandler,
} from '@material-bank/mtag-forms'
import { MbLogo } from '@material-bank/mtag-icons'
import { useTranslations } from '@material-bank/i18n'

function getEmailStepSchema(emailValidationMessage: string) {
  return z.object({
    email: z.string().email(emailValidationMessage),
  })
}

type EmailStepData = z.infer<ReturnType<typeof getEmailStepSchema>>

type EmailStepProps = {
  isLoading: boolean
  error: string | null
  onSubmit: (email: string) => void
}

export function EmailStep({ isLoading, error, onSubmit }: EmailStepProps) {
  const t = useTranslations('authentication.emailStep')
  const emailStepSchema = getEmailStepSchema(t('emailValidation'))

  const submitHandler: SubmitHandler<EmailStepData> = (data) => {
    onSubmit(data.email)
  }

  return (
    <div className="flex flex-col gap-10">
      <div className="flex items-center gap-3">
        <div className="border border-gray-300 rounded p-2">
          <MbLogo width={32} height={32} />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <H3>{t('heading')}</H3>
        <Body variant="small" className="text-gray-500">
          {t('subheading')}
        </Body>
      </div>

      <Form
        formProps={createFormSchema(emailStepSchema)}
        onSubmit={submitHandler}
        className="flex flex-col gap-4"
      >
        <Input
          name="email"
          label={t('emailLabel')}
          type="email"
          placeholder={t('emailPlaceholder')}
          disabled={isLoading}
          autoFocus
          autoComplete="email"
        />

        {error && (
          <Body variant="small" className="text-red-600">
            {error}
          </Body>
        )}

        <Button
          type="submit"
          variant="default"
          size="lg"
          disabled={isLoading}
          className="w-full"
        >
          {isLoading ? t('submitButtonLoading') : t('submitButton')}
        </Button>
      </Form>

      <Caption className="text-gray-400 text-center">
        {t('termsAgreement')}
      </Caption>
    </div>
  )
}
